import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import MainNav from '../../components/MainNav/MainNav';
import Footer from '../../components/Footer/Footer';
import { getAllPosts, getFeaturedPosts, getAllCategories } from '../../data/blogPosts';
import './BlogPage.css';

const BlogPage = ({ darkMode, toggleDarkMode }) => {
  const [selectedCategory, setSelectedCategory] = useState('Todas');
  const [searchQuery, setSearchQuery] = useState('');
  
  const allPosts = getAllPosts();
  const featuredPosts = getFeaturedPosts();
  const categories = ['Todas', ...getAllCategories()];

  // Filtrar posts por categoría y búsqueda
  const filteredPosts = allPosts.filter(post => {
    const matchesCategory = selectedCategory === 'Todas' || post.categoria === selectedCategory;
    const matchesSearch = searchQuery === '' || 
      post.titulo.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.extracto.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('es-ES', options);
  };

  return (
    <>
      <SEO 
        title="Blog | Consultoría tecnológica, IA y software de gestión | E-ASY"
        description="Guías sobre consultoría tecnológica, ERP, integración de sistemas e inteligencia artificial para empresas en España. La sede de E-ASY está en Tenerife."
        keywords="consultoría tecnológica, ERP para pymes, software de gestión, integración de sistemas, inteligencia artificial empresas, E-ASY"
        canonical="https://e-asy.es/blog"
      />
      <MainNav darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
      
      <div className={`blog-page ${darkMode ? 'dark' : 'light'}`}>
        {/* Hero Section */}
        <section className="blog-hero">
          <div className="blog-hero-content">
            <h1 className="blog-hero-title">Blog E-ASY</h1>
            <p className="blog-hero-subtitle">
              Guías sobre consultoría tecnológica, software de gestión e inteligencia artificial para empresas en España
            </p>
            
            {/* Buscador */}
            <div className="blog-search">
              <input
                type="text"
                placeholder="Buscar artículos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="blog-search-input"
              />
              <span className="blog-search-icon">🔍</span>
            </div>
          </div>
        </section>

        {/* Posts Destacados */}
        {searchQuery === '' && selectedCategory === 'Todas' && (
          <section className="blog-featured">
            <div className="blog-featured-content">
              <h2 className="blog-section-title">Artículos Destacados</h2>
              <div className="blog-featured-grid">
                {featuredPosts.slice(0, 3).map(post => (
                  <Link to={`/blog/${post.slug}`} key={post.id} className="blog-featured-card">
                    <div className="blog-featured-badge">Destacado</div>
                    <div className="blog-featured-category">{post.categoria}</div>
                    <h3 className="blog-featured-title">{post.titulo}</h3>
                    <p className="blog-featured-excerpt">{post.extracto}</p>
                    <div className="blog-featured-meta">
                      <span className="blog-featured-author">{post.autor}</span>
                      <span className="blog-featured-date">{formatDate(post.fecha)}</span>
                      <span className="blog-featured-time">{post.tiempoLectura} min lectura</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Filtros por Categoría */}
        <section className="blog-filters">
          <div className="blog-filters-content">
            <div className="blog-categories">
              {categories.map(category => (
                <button
                  key={category}
                  className={`blog-category-btn ${selectedCategory === category ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(category)}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Lista de Posts */}
        <section className="blog-list">
          <div className="blog-list-content">
            <h2 className="blog-section-title">
              {selectedCategory === 'Todas' ? 'Todos los Artículos' : selectedCategory}
              {searchQuery && ` - Resultados para "${searchQuery}"`}
            </h2>
            
            {filteredPosts.length === 0 ? (
              <div className="blog-no-results">
                <p>No se encontraron artículos que coincidan con tu búsqueda.</p>
                <button 
                  className="blog-reset-btn"
                  onClick={() => { setSearchQuery(''); setSelectedCategory('Todas'); }}
                >
                  Ver todos los artículos
                </button>
              </div>
            ) : (
              <div className="blog-grid">
                {filteredPosts.map(post => (
                  <article key={post.id} className="blog-card">
                    <div className="blog-card-header">
                      <span className="blog-card-category">{post.categoria}</span>
                      <span className="blog-card-time">{post.tiempoLectura} min</span>
                    </div>
                    <h3 className="blog-card-title">
                      <Link to={`/blog/${post.slug}`}>{post.titulo}</Link>
                    </h3>
                    <p className="blog-card-excerpt">{post.extracto}</p>
                    <div className="blog-card-tags">
                      {post.tags.slice(0, 3).map(tag => (
                        <span key={tag} className="blog-card-tag">{tag}</span>
                      ))}
                    </div>
                    <div className="blog-card-footer">
                      <div className="blog-card-author">
                        <span className="blog-card-author-name">{post.autor}</span>
                      </div>
                      <span className="blog-card-date">{formatDate(post.fecha)}</span>
                    </div>
                    <Link to={`/blog/${post.slug}`} className="blog-card-link">
                      Leer artículo →
                    </Link>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="blog-cta">
          <div className="blog-cta-content">
            <h2 className="blog-cta-title">¿Quieres implementar estas soluciones en tu empresa?</h2>
            <p className="blog-cta-subtitle">
              En E-ASY ayudamos a PYMEs canarias a transformarse digitalmente con 
              IA generativa, automatización y consultoría tecnológica a medida.
            </p>
            <div className="blog-cta-buttons">
              <Link to="/contacto" className="blog-cta-btn primary">
                Solicitar consultoría gratuita
              </Link>
              <Link to="/servicios" className="blog-cta-btn secondary">
                Ver nuestros servicios
              </Link>
            </div>
          </div>
        </section>
      </div>
      
      <Footer darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
    </>
  );
};

export default BlogPage;



